import { useState, useEffect, useCallback } from "react";
import {
  Box, Card, CardContent, Typography, Button, Alert, LinearProgress,
  Chip, Dialog, DialogContent, Paper,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import WarningIcon from "@mui/icons-material/Warning";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useDropzone } from "react-dropzone";
import { ServerConfig } from "../../../connectors/Config";
import { uploadFileToS3 } from "../../../utils/s3Uploader";

const API = ServerConfig.AWS.Serverless.API_BASE_URL_2;

/**
 * Gestione documenti richiesti di un discente (Decreto D.Dir. 1986/2024).
 * Upload con drag&drop (react-dropzone) verso S3 tramite s3Uploader, quindi
 * registrazione dei metadati sull'endpoint /decreto/documenti-discente.
 * Mostra lo stato di validazione (mancante / in verifica / validato / rifiutato)
 * e un riepilogo globale.
 *
 * @param {string} profileId - id del profilo (profiles.id) del discente.
 */
const DocumentiDiscente = ({ profileId }) => {
  const [documenti, setDocumenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewDialog, setPreviewDialog] = useState({ open: false, url: "" });
  const [stato, setStato] = useState({});
  const [errore, setErrore] = useState("");

  const loadDocumenti = useCallback(async () => {
    if (!profileId) { setLoading(false); return; }
    try {
      const res = await fetch(`${API}/decreto/documenti-discente?profile_id=${profileId}`);
      const json = await res.json();
      setDocumenti(Array.isArray(json?.documenti) ? json.documenti : []);
    } catch (e) {
      console.error("Errore caricamento documenti", e);
      setErrore("Errore nel caricamento dei documenti.");
    } finally {
      setLoading(false);
    }
  }, [profileId]);

  const loadStato = useCallback(async () => {
    if (!profileId) return;
    try {
      const res = await fetch(`${API}/decreto/stato-validazione?profile_id=${profileId}`);
      const json = await res.json();
      setStato(json || {});
    } catch (e) {
      console.error("Errore stato validazione", e);
    }
  }, [profileId]);

  useEffect(() => {
    setLoading(true);
    loadDocumenti();
    loadStato();
  }, [loadDocumenti, loadStato]);

  const handleUpload = async (documentoRichiestoId, file) => {
    setErrore("");
    setUploadingDoc(documentoRichiestoId);
    setUploadProgress(10);
    // Progress indicativo durante l'upload verso S3.
    let progress = 10;
    const interval = setInterval(() => {
      progress = Math.min(progress + 10, 90);
      setUploadProgress(progress);
    }, 200);
    try {
      const url = await uploadFileToS3(file, {
        prefix: "documento-decreto",
        maxSizeMB: 5,
        allowedTypes: ["application/pdf", "image/jpeg", "image/png"],
      });
      const res = await fetch(`${API}/decreto/documenti-discente`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          profile_id: profileId,
          documento_richiesto_id: documentoRichiestoId,
          filename: file.name,
          file_path: url,
          file_size: file.size,
          mime_type: file.type,
        }),
      });
      clearInterval(interval);
      if (res.ok) {
        setUploadProgress(100);
        await loadDocumenti();
        await loadStato();
      } else {
        const err = await res.json().catch(() => ({}));
        setErrore(err?.error || "Errore durante la registrazione del documento.");
      }
    } catch (e) {
      clearInterval(interval);
      setErrore(e?.message || "Errore durante l'upload.");
    } finally {
      setTimeout(() => {
        setUploadingDoc(null);
        setUploadProgress(0);
      }, 600);
    }
  };

  const getStatoIcon = (doc) => {
    if (!doc.file_path) return <WarningIcon color="error" />;
    switch (doc.stato_validazione) {
      case "approved": return <CheckCircleIcon color="success" />;
      case "rejected": return <CancelIcon color="error" />;
      case "pending": return <HourglassEmptyIcon color="warning" />;
      default: return <CheckCircleIcon color="info" />;
    }
  };
  const getStatoLabel = (doc) => {
    if (!doc.file_path) return "Mancante";
    switch (doc.stato_validazione) {
      case "approved": return "Validato";
      case "rejected": return "Rifiutato";
      case "pending": return "In Verifica";
      default: return "Caricato";
    }
  };
  const getStatoColor = (doc) => {
    if (!doc.file_path) return "error";
    switch (doc.stato_validazione) {
      case "approved": return "success";
      case "rejected": return "error";
      case "pending": return "warning";
      default: return "info";
    }
  };
  const isScaduto = (doc) => doc.data_scadenza && new Date(doc.data_scadenza) < new Date();

  const DocumentoCard = ({ documento }) => {
    const onDrop = useCallback((acceptedFiles) => {
      if (acceptedFiles.length > 0) handleUpload(documento.id, acceptedFiles[0]);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [documento.id]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      onDrop,
      accept: {
        "application/pdf": [".pdf"],
        "image/jpeg": [".jpg", ".jpeg"],
        "image/png": [".png"],
      },
      maxSize: 5242880,
      multiple: false,
      disabled: uploadingDoc === documento.id,
    });

    const mostraDropzone = !documento.file_path || documento.stato_validazione === "rejected";

    return (
      <Card sx={{ mb: 2, border: isDragActive ? "2px dashed #1976d2" : "1px solid #e0e0e0" }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box flex={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="h6">{documento.nome_documento}</Typography>
                {documento.obbligatorio && <Chip label="Obbligatorio" size="small" color="error" />}
              </Box>
              {documento.descrizione && (
                <Typography variant="body2" color="textSecondary" mt={0.5}>
                  {documento.descrizione}
                </Typography>
              )}
              {documento.formato_accettato && (
                <Typography variant="caption" color="textSecondary">
                  Formati accettati: {documento.formato_accettato}
                </Typography>
              )}
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              {getStatoIcon(documento)}
              <Chip label={getStatoLabel(documento)} color={getStatoColor(documento)} size="small" />
            </Box>
          </Box>

          {documento.data_scadenza && (
            <Alert severity={isScaduto(documento) ? "error" : "warning"} sx={{ mb: 2 }}>
              {isScaduto(documento) ? "SCADUTO" : "Scadenza"}:{" "}
              {new Date(documento.data_scadenza).toLocaleDateString("it-IT")}
            </Alert>
          )}

          {documento.note_validazione && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <strong>Motivo rifiuto:</strong> {documento.note_validazione}
            </Alert>
          )}

          {mostraDropzone ? (
            <Paper {...getRootProps()} sx={{
              p: 3, textAlign: "center", border: "2px dashed",
              borderColor: isDragActive ? "primary.main" : "grey.300",
              bgcolor: isDragActive ? "action.hover" : "background.paper",
              cursor: "pointer", transition: "all 0.3s",
              "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" },
            }}>
              <input {...getInputProps()} />
              <CloudUploadIcon sx={{ fontSize: 48, color: "text.secondary", mb: 1 }} />
              <Typography variant="body1" gutterBottom>
                {isDragActive
                  ? "Rilascia il file qui..."
                  : "Trascina qui il documento oppure clicca per selezionare"}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                PDF, JPG, PNG (max 5MB)
              </Typography>
            </Paper>
          ) : (
            <Box display="flex" gap={1} justifyContent="flex-end">
              <Button variant="outlined" size="small" startIcon={<VisibilityIcon />}
                onClick={() => setPreviewDialog({ open: true, url: documento.file_path })}>
                Visualizza
              </Button>
              <Button variant="outlined" size="small" startIcon={<DownloadIcon />}
                onClick={() => window.open(documento.file_path, "_blank")}>
                Scarica
              </Button>
            </Box>
          )}

          {uploadingDoc === documento.id && (
            <Box mt={2}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography variant="caption" color="textSecondary" mt={0.5}>
                Caricamento in corso... {uploadProgress}%
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) return <LinearProgress />;

  const totali = stato?.richiesti?.length || documenti.length || 0;
  const validati = (documenti || []).filter((d) => d.file_path).length;
  const completo = stato?.completo === true;

  return (
    <Box>
      {errore && <Alert severity="error" sx={{ mb: 2 }}>{errore}</Alert>}

      {/* STATO VALIDAZIONE GLOBALE */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Stato Validazione Documenti</Typography>
          <Box display="flex" gap={2} alignItems="center" mt={2} flexWrap="wrap">
            <Chip label={completo ? "COMPLETO" : "INCOMPLETO"}
              color={completo ? "success" : "error"} />
            <Typography variant="body2">
              {validati} / {totali} documenti caricati
            </Typography>
          </Box>
          {Array.isArray(stato?.mancanti) && stato.mancanti.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <strong>Documenti obbligatori mancanti:</strong>
              <ul style={{ margin: "6px 0 0 0" }}>
                {stato.mancanti.map((doc, idx) => (
                  <li key={idx}>{doc.nome_documento || doc.nome}</li>
                ))}
              </ul>
            </Alert>
          )}
        </CardContent>
      </Card>

      <Typography variant="h5" gutterBottom>Documenti Richiesti</Typography>

      {documenti.length === 0 && (
        <Alert severity="info">
          Nessun documento richiesto: selezionare prima una tipologia discente
          nella scheda "Anagrafica Decreto".
        </Alert>
      )}

      {documenti.map((doc) => (
        <DocumentoCard key={doc.id} documento={doc} />
      ))}

      <Dialog open={previewDialog.open} onClose={() => setPreviewDialog({ open: false, url: "" })}
        maxWidth="md" fullWidth>
        <DialogContent>
          <iframe src={previewDialog.url} width="100%" height="600px" title="Anteprima Documento" />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default DocumentiDiscente;
